import type { Farm, ProductionUnit } from '../types/farm'
import type { FacilityType, WizardFarm, WizardProductionUnit } from '../types/farmWizard'
export type { WizardFarm, WizardProductionUnit } from '../types/farmWizard'

import { ensureOfflineReady } from '../offline/initOffline'
import { getOfflineDb } from '../offline/dbFactory'
import { dexieFarmRepo } from '../offline/repos/DexieFarmRepo'
import { dexieLotRepo } from '../offline/repos/DexieLotRepo'
import { getTenantScopedKey } from '../offline/tenant'
import { api } from './api'
import { deleteKey, loadJSON, saveJSON } from './storage'

const CURRENT_FARM_KEY = 'currentFarm'

const hasStorage = (): boolean => typeof globalThis !== 'undefined' && Boolean(globalThis.localStorage)

const readJson = <T>(key: string): T | null => {
  if (!hasStorage()) return null
  return loadJSON<T | null>(key, null)
}

const writeJson = (key: string, value: unknown): void => {
  if (!hasStorage()) return
  if (value === null || value === undefined) {
    deleteKey(key)
    return
  }
  saveJSON(key, value)
}

const parseCapacity = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return undefined
}

const sanitizeSpecies = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined
  return value.filter((item): item is string => typeof item === 'string')
}

const sanitizeFacilityType = (value: unknown): FacilityType | undefined => (
  value === 'processing-plant' || value === 'farm'
    ? value
    : undefined
)

export const fromApiProductionUnit = (unit: ProductionUnit): WizardProductionUnit => {
  const metadata = unit.metadata ?? {}
  const legacyWizardMetadata = typeof metadata.wizard === 'object' && metadata.wizard !== null
    ? metadata.wizard as Record<string, unknown>
    : {}

  let resolvedType: string | undefined
  if (typeof metadata.type === 'string') {
    resolvedType = metadata.type
  } else if (typeof legacyWizardMetadata.type === 'string') {
    resolvedType = legacyWizardMetadata.type
  }

  const resolvedCapacity = parseCapacity(metadata.capacity ?? legacyWizardMetadata.capacity)
  let resolvedCapacityUnit: string | undefined
  if (typeof metadata.capacityUnit === 'string') {
    resolvedCapacityUnit = metadata.capacityUnit
  } else if (typeof legacyWizardMetadata.capacityUnit === 'string') {
    resolvedCapacityUnit = legacyWizardMetadata.capacityUnit
  }

  return {
    ...unit,
    type: resolvedType,
    capacity: resolvedCapacity,
    capacityUnit: resolvedCapacityUnit
  }
}

export const fromApiFarm = (farm: Farm): WizardFarm => {
  const metadata = farm.metadata ?? {}
  return {
    ...farm,
    facilityType: sanitizeFacilityType(metadata.facilityType) ?? 'farm',
    classification: typeof metadata.classification === 'string' ? metadata.classification : undefined,
    geography: typeof metadata.geography === 'string' ? metadata.geography : undefined,
    typeCulture: typeof metadata.typeCulture === 'string' ? metadata.typeCulture : undefined,
    stage: typeof metadata.stage === 'string' ? metadata.stage : undefined,
    species: sanitizeSpecies(metadata.species),
    productionUnits: (farm.productionUnits ?? []).map(fromApiProductionUnit)
  }
}

const compactFarmForStorage = (farm: WizardFarm): WizardFarm => {
  const productionUnits = Array.isArray(farm.productionUnits)
    ? farm.productionUnits
      .filter((unit) => Boolean(unit) && typeof unit.id === 'string')
      .map((unit: WizardProductionUnit) => {
        const metadata = buildProductionUnitMetadata(unit, unit)
        return {
          id: unit.id,
          farmId: unit.farmId,
          name: unit.name,
          altitude: unit.altitude,
          metadata,
          type: unit.type,
          capacity: unit.capacity,
          capacityUnit: unit.capacityUnit
        }
      })
    : undefined

  return {
    id: farm.id,
    name: farm.name,
    altitude: farm.altitude,
    location: farm.location,
    metadata: farm.metadata,
    facilityType: farm.facilityType,
    classification: farm.classification,
    geography: farm.geography,
    typeCulture: farm.typeCulture,
    species: farm.species,
    stage: farm.stage,
    productionUnits
  }
}

const cloneMetadata = (value?: Record<string, unknown>): Record<string, unknown> => (value ? { ...value } : {})

const applyField = (metadata: Record<string, unknown>, key: string, value: unknown): void => {
  if (value === undefined || value === null || value === '') {
    delete metadata[key]
  } else {
    metadata[key] = value
  }
}

export const buildFarmMetadata = (
  base: WizardFarm | null,
  patch?: Partial<WizardFarm>
): Record<string, unknown> => {
  const metadata = cloneMetadata(base?.metadata)
  const merged: Partial<WizardFarm> = { ...(base ?? undefined), ...(patch ?? undefined) }

  metadata.facilityType = sanitizeFacilityType(merged.facilityType) ?? 'farm'

  applyField(metadata, 'classification', merged.classification)
  applyField(metadata, 'geography', merged.geography)
  applyField(metadata, 'typeCulture', merged.typeCulture)
  applyField(metadata, 'stage', merged.stage)

  if (Array.isArray(merged.species) && merged.species.length > 0) {
    metadata.species = merged.species.slice()
  } else {
    delete metadata.species
  }

  return metadata
}

export const buildProductionUnitMetadata = (
  base: WizardProductionUnit | null,
  patch?: Partial<WizardProductionUnit>
): Record<string, unknown> => {
  const metadata = cloneMetadata(base?.metadata)
  const merged: Partial<WizardProductionUnit> = { ...(base ?? undefined), ...(patch ?? undefined) }

  applyField(metadata, 'type', merged.type)

  const capacity = typeof merged.capacity === 'number' && Number.isFinite(merged.capacity)
    ? merged.capacity
    : undefined
  if (capacity === undefined) {
    delete metadata.capacity
  } else {
    metadata.capacity = capacity
  }

  applyField(metadata, 'capacityUnit', merged.capacityUnit)

  return metadata
}

const currentFarmKey = (): string => getTenantScopedKey(CURRENT_FARM_KEY)

export const readCurrentFarm = (): WizardFarm | null => readJson<WizardFarm>(currentFarmKey())

export const writeCurrentFarm = (farm: WizardFarm | null): WizardFarm | null => {
  if (farm) {
    writeJson(currentFarmKey(), compactFarmForStorage(farm))
    return farm
  }
  writeJson(currentFarmKey(), null)
  return null
}

export const updateCurrentFarm = (
  updater: (prev: WizardFarm | null) => WizardFarm | null
): WizardFarm | null => {
  const next = updater(readCurrentFarm())
  if (next) {
    // Keep wizard fields mirrored into farm.metadata so the persisted farm record
    // (and any downstream screens) can rely on metadata as the canonical source.
    next.metadata = buildFarmMetadata(next, next)
    writeCurrentFarm(next)
  } else {
    writeJson(currentFarmKey(), null)
  }
  return next ?? null
}

const sortFarms = (farms: WizardFarm[]): WizardFarm[] => (
  farms.slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
)

const mergeRemoteProductionUnitsIntoFarms = (
  farms: WizardFarm[],
  units: WizardProductionUnit[]
): WizardFarm[] => {
  const unitsByFarmId = units.reduce<Map<string, WizardProductionUnit[]>>((acc, unit) => {
    const farmId = String(unit.farmId ?? '').trim()
    if (!farmId) return acc
    const existing = acc.get(farmId) ?? []
    existing.push(unit)
    acc.set(farmId, existing)
    return acc
  }, new Map())

  return farms.map((farm) => ({
    ...farm,
    productionUnits: unitsByFarmId.get(String(farm.id)) ?? farm.productionUnits ?? []
  }))
}

const getPendingFarmRefreshIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  const pendingFarmIds = new Set<string>()

  for (const entry of entries) {
    if (entry.entityType === 'farm') {
      pendingFarmIds.add(entry.entityId)
      continue
    }

    if (entry.entityType !== 'productionUnit') continue

    const farmId = typeof (entry.payload as any)?.farmId === 'string'
      ? (entry.payload as any).farmId.trim()
      : ''
    if (farmId) {
      pendingFarmIds.add(farmId)
    }
  }

  return pendingFarmIds
}

const canAttemptNetwork = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const repairFarmsMissingProductionUnitsFromLots = async (farms: WizardFarm[]): Promise<WizardFarm[]> => {
  const missing = farms.filter((farm) => (farm.productionUnits ?? []).length === 0)
  if (missing.length === 0) return farms

  let lots: Array<{ farmId: string; productionUnitId?: string }> = []
  try {
    lots = await dexieLotRepo.list()
  } catch {
    lots = []
  }

  if (lots.length === 0) return farms

  const unitIdsByFarmId = new Map<string, Set<string>>()
  lots.forEach((lot) => {
    const farmId = String((lot as any)?.farmId ?? '').trim()
    const productionUnitId = String((lot as any)?.productionUnitId ?? '').trim()
    if (!farmId || !productionUnitId) return

    let set = unitIdsByFarmId.get(farmId)
    if (!set) {
      set = new Set()
      unitIdsByFarmId.set(farmId, set)
    }
    set.add(productionUnitId)
  })

  let changed = false
  const repaired = farms.map((farm) => {
    const existingUnits = farm.productionUnits ?? []
    if (existingUnits.length > 0) return farm

    const derivedUnitIds = Array.from(unitIdsByFarmId.get(String(farm.id))?.values() ?? [])
      .sort((a, b) => a.localeCompare(b))
    const derivedUnits: WizardProductionUnit[] = derivedUnitIds.map((unitId, index) => ({
      id: unitId,
      farmId: String(farm.id),
      name: `PU ${index + 1}`
    }))
    if (derivedUnits.length === 0) return farm

    changed = true
    return {
      ...farm,
      productionUnits: derivedUnits
    }
  })

  if (changed) {
    try {
      await dexieFarmRepo.upsertMany(repaired)
    } catch {
      // ignore best-effort
    }
  }

  return repaired
}

const refreshProductionUnitsForCachedFarms = async (farms: WizardFarm[]): Promise<WizardFarm[]> => {
  const missing = farms.filter((farm) => (farm.productionUnits ?? []).length === 0)
  if (missing.length === 0) return farms
  if (!canAttemptNetwork()) return farms

  try {
    const units = await api.productionUnits.list({ noCache: true })
    const unitsByFarmId = units.reduce<Record<string, WizardProductionUnit[]>>((acc, unit) => {
      const farmId = String((unit as any)?.farmId ?? '').trim()
      if (!farmId) return acc
      if (!acc[farmId]) acc[farmId] = []
      acc[farmId].push(fromApiProductionUnit(unit as any))
      return acc
    }, {})

    let changed = false
    const updated = farms.map((farm) => {
      const existingUnits = farm.productionUnits ?? []
      if (existingUnits.length > 0) return farm

      const resolvedUnits = unitsByFarmId[String(farm.id)] ?? []
      if (resolvedUnits.length === 0) return farm

      changed = true
      return {
        ...farm,
        productionUnits: resolvedUnits
      }
    })

    if (changed) {
      try {
        await dexieFarmRepo.upsertMany(updated)
      } catch {
        // ignore
      }
    }

    return updated
  } catch (error) {
    console.warn('[farmWizard] failed to refresh missing production units', error)
    return farms
  }
}

export const refreshFarmListFromApi = async (): Promise<WizardFarm[]> => {
  await ensureOfflineReady()

  const [cached, remoteFarms, remoteUnits, pendingFarmIds] = await Promise.all([
    dexieFarmRepo.list(),
    api.farms.list({ noCache: true }),
    api.productionUnits.list({ noCache: true }),
    getPendingFarmRefreshIds()
  ])

  const remoteMerged = mergeRemoteProductionUnitsIntoFarms(
    remoteFarms.map(fromApiFarm),
    remoteUnits.map(fromApiProductionUnit)
  )

  const nextById = new Map<string, WizardFarm>()

  for (const farm of cached) {
    if (pendingFarmIds.has(farm.id)) {
      nextById.set(farm.id, farm)
    }
  }

  for (const farm of remoteMerged) {
    if (pendingFarmIds.has(farm.id)) continue
    nextById.set(farm.id, farm)
  }

  const next = sortFarms(Array.from(nextById.values()))
  await dexieFarmRepo.replaceAll(next)
  return next
}

export const ensureFarmList = async (options: { forceRefresh?: boolean } = {}): Promise<WizardFarm[]> => {
  const { forceRefresh = false } = options
  await ensureOfflineReady()
  const cached = await dexieFarmRepo.list()

  if (canAttemptNetwork()) {
    try {
      const refreshed = await refreshFarmListFromApi()
      return await repairFarmsMissingProductionUnitsFromLots(refreshed)
    } catch (error) {
      console.warn('[farmWizard] failed to pull fresh farms from API', error)
    }
  }

  if (!forceRefresh && cached.length > 0) {
    const repairedFromLots = await repairFarmsMissingProductionUnitsFromLots(cached)
    return await refreshProductionUnitsForCachedFarms(repairedFromLots)
  }

  try {
    const refreshed = await refreshFarmListFromApi()
    return await repairFarmsMissingProductionUnitsFromLots(refreshed)
  } catch (error) {
    console.error('[farmWizard] failed to refresh farm list', error)
    return await repairFarmsMissingProductionUnitsFromLots(cached)
  }
}

export const findFarmById = (farms: WizardFarm[], farmId?: string | null): WizardFarm | undefined => {
  if (!farmId) return undefined
  return farms.find((farm) => farm.id === farmId)
}

export const findProductionUnitById = (
  farm: WizardFarm | undefined,
  unitId?: string | null
): WizardProductionUnit | undefined => {
  if (!farm || !unitId) return undefined
  return (farm.productionUnits ?? []).find((unit) => unit.id === unitId)
}
